import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { ClassTimes, ExamSchedule, LessonLog, LessonOverride, MakeupSchedule, TestRecord } from '@/types';
import { fetchOverridesForRange } from '@/lib/lessonOverrides';
import { getWeek } from '@/lib/datetime';
import WeekScheduleCard, { type ScheduleStudent } from '@/pages/dashboard/components/WeekScheduleCard';
import RecentLessonsCard, { type RecentLesson } from '@/pages/dashboard/components/RecentLessonsCard';
import UpcomingExamsCard, { type UpcomingExam } from '@/pages/dashboard/components/UpcomingExamsCard';
import UpcomingMakeupCard, { type UpcomingMakeup } from '@/pages/dashboard/components/UpcomingMakeupCard';
import OverdueMakeupAlert, { type OverdueMakeup } from '@/pages/dashboard/components/OverdueMakeupAlert';
import TodayLessonsCard from '@/pages/dashboard/components/TodayLessonsCard';
import TodayTodosCard, { type TodoExam, type TodoStudent } from '@/pages/dashboard/components/TodayTodosCard';
import { tuitionStatus } from '@/lib/datetime';
import QuickActionsCard from '@/pages/dashboard/components/QuickActionsCard';
import MonthlyReportModal from '@/pages/dashboard/components/MonthlyReportModal';

interface StatCard {
  label: string;
  value: number | string;
  unit: string;
  icon: string;
  tone: 'primary' | 'accent' | 'secondary';
  to: string;
}

interface StudentRow {
  id: string;
  name: string;
  class_days: string[] | null;
  class_times: ClassTimes | null;
  class_start_time: string | null;
  class_end_time: string | null;
  status: string | null;
  tuition_due_day: number | null;
  tuition_last_paid_at: string | null;
  first_lesson_date: string | null;
}

export default function DashboardPage() {
  const { profile } = useAuth();
  const [stats, setStats] = useState<StatCard[]>([]);
  const [students, setStudents] = useState<ScheduleStudent[]>([]);
  const [recentLessons, setRecentLessons] = useState<RecentLesson[]>([]);
  const [exams, setExams] = useState<UpcomingExam[]>([]);
  const [makeups, setMakeups] = useState<UpcomingMakeup[]>([]);
  const [overdueMakeups, setOverdueMakeups] = useState<OverdueMakeup[]>([]);
  const [todayWrittenIds, setTodayWrittenIds] = useState<string[]>([]);
  const [weekOverrides, setWeekOverrides] = useState<LessonOverride[]>([]);
  const [unpaidStudents, setUnpaidStudents] = useState<TodoStudent[]>([]);
  const [tomorrowExams, setTomorrowExams] = useState<TodoExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const today = now.toISOString().slice(0, 10);

      const currentWeek = getWeek(0);
      const [studentsRes, examsRes, namesRes, recentRes, makeupsRes, overdueRes, todayLogsRes] = await Promise.all([
        supabase
          .from('students')
          .select('id, name, class_days, class_times, class_start_time, class_end_time, status, tuition_due_day, tuition_last_paid_at, first_lesson_date'),
        supabase
          .from('exam_schedules')
          .select('*')
          .gte('end_date', today)
          .order('start_date', { ascending: true })
          .limit(5),
        supabase.from('students').select('id, name'),
        supabase.from('lesson_logs').select('*').order('lesson_date', { ascending: false }).limit(5),
        supabase
          .from('makeup_schedules')
          .select('*')
          .is('lesson_log_id', null)
          .gte('scheduled_date', today)
          .order('scheduled_date', { ascending: true })
          .limit(5),
        supabase
          .from('makeup_schedules')
          .select('*')
          .is('lesson_log_id', null)
          .lt('scheduled_date', today)
          .order('scheduled_date', { ascending: true }),
        supabase.from('lesson_logs').select('student_id').eq('lesson_date', today),
      ]);

      if (studentsRes.error) throw studentsRes.error;

      const studentRows = (studentsRes.data as StudentRow[]) ?? [];
      const examList = (examsRes.data as ExamSchedule[]) ?? [];
      const recentRows = (recentRes.data as LessonLog[]) ?? [];
      const makeupList = (makeupsRes.data as MakeupSchedule[]) ?? [];
      const overdueList = (overdueRes.data as MakeupSchedule[]) ?? [];

      const nameMap = new Map<string, string>();
      ((namesRes.data as { id: string; name: string }[]) ?? []).forEach((s) => {
        nameMap.set(s.id, s.name);
      });

      let testsByLesson = new Map<string, TestRecord[]>();
      const lessonIds = recentRows.map((l) => l.id);
      if (lessonIds.length > 0) {
        const testsRes = await supabase
          .from('test_records')
          .select('*')
          .in('lesson_log_id', lessonIds);
        testsByLesson = new Map<string, TestRecord[]>();
        ((testsRes.data as TestRecord[]) ?? []).forEach((t) => {
          if (!t.lesson_log_id) return;
          const list = testsByLesson.get(t.lesson_log_id) ?? [];
          list.push(t);
          testsByLesson.set(t.lesson_log_id, list);
        });
      }

      const toRecent = (l: LessonLog): RecentLesson => ({
        ...l,
        studentName: nameMap.get(l.student_id) ?? '학생',
        tests: testsByLesson.get(l.id) ?? [],
      });

      const activeStudents = studentRows.filter((s) => s.status !== 'ended');
      const unpaidList = activeStudents.filter(
        (s) => tuitionStatus(s.tuition_due_day, s.tuition_last_paid_at).status === 'overdue',
      );
      setUnpaidStudents(unpaidList.map((s) => ({ id: s.id, name: s.name })));

      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().slice(0, 10);
      setTomorrowExams(
        examList
          .filter((e) => e.start_date === tomorrowStr)
          .map((e) => ({
            id: e.id,
            studentId: e.student_id,
            studentName: nameMap.get(e.student_id) ?? '학생',
            title: e.title,
          })),
      );

      setStats([
        { label: '관리 중인 학생', value: activeStudents.length, unit: '명', icon: 'ri-user-3-line', tone: 'primary', to: '/students' },
        { label: '미납 학생', value: unpaidList.length, unit: '명', icon: 'ri-error-warning-line', tone: 'accent', to: '/tuition' },
        { label: '다가오는 시험', value: examList.length, unit: '건', icon: 'ri-calendar-event-line', tone: 'secondary', to: '/exams' },
      ]);

      setStudents(
        studentRows.map((s) => ({
          id: s.id,
          name: s.name,
          class_days: s.class_days ?? [],
          class_times: s.class_times,
          class_start_time: s.class_start_time,
          class_end_time: s.class_end_time,
          first_lesson_date: s.first_lesson_date,
        })),
      );

      setRecentLessons(recentRows.map(toRecent));
      setExams(examList.map((e) => ({ ...e, studentName: nameMap.get(e.student_id) ?? '학생' })));
      setMakeups(
        makeupList.map((m) => ({ ...m, studentName: nameMap.get(m.student_id) ?? '학생' })),
      );
      setOverdueMakeups(
        overdueList.map((m) => ({ ...m, studentName: nameMap.get(m.student_id) ?? '학생' })),
      );
      setTodayWrittenIds(
        ((todayLogsRes.data as { student_id: string }[]) ?? []).map((l) => l.student_id),
      );

      try {
        const ov = await fetchOverridesForRange(currentWeek[0].date, currentWeek[6].date);
        setWeekOverrides(ov);
      } catch {
        setWeekOverrides([]);
      }
    } catch {
      setError('대시보드 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toneClass: Record<StatCard['tone'], string> = {
    primary: 'bg-primary-100 text-primary-600',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-800',
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <div className="rounded-2xl border border-background-200 bg-gradient-to-br from-primary-50 to-background-100 p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-primary-700">오늘도 화이팅이에요</p>
            <h1 className="mt-1.5 text-2xl font-bold text-foreground-950 md:text-3xl">
              {profile?.name ? `${profile.name} 선생님, 환영합니다` : '환영합니다'}
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              이번 주 수업일정과 최근 수업일지를 한눈에 확인하고, 월간 리포트로 내보내 보관할 수
              있어요.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="no-print flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-file-chart-line"></i>
            월간 리포트 내보내기
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
          불러오는 중...
        </div>
      ) : error ? (
        <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16">
          <i className="ri-error-warning-line text-2xl text-accent-600"></i>
          <p className="text-sm text-foreground-700">{error}</p>
          <button
            type="button"
            onClick={load}
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      ) : (
        <>
          {overdueMakeups.length > 0 && (
            <div className="mt-6">
              <OverdueMakeupAlert makeups={overdueMakeups} />
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
            {stats.map((s) => (
              <Link
                key={s.label}
                to={s.to}
                className="group block rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100 sm:p-5"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs leading-snug text-foreground-600 sm:text-sm">
                    {s.label}
                  </span>
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9 ${toneClass[s.tone]}`}
                  >
                    <i className={s.icon}></i>
                  </span>
                </div>
                <p className="mt-2.5 text-2xl font-bold text-foreground-950 sm:mt-3 sm:text-3xl">
                  {s.value}
                  <span className="ml-1 text-sm font-medium text-foreground-500 sm:text-base">
                    {s.unit}
                  </span>
                </p>
                <span className="mt-1.5 hidden items-center gap-1 text-xs font-semibold text-primary-600 sm:mt-2 sm:inline-flex">
                  자세히 보기
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-6">
            <TodayTodosCard
              students={students}
              writtenStudentIds={todayWrittenIds}
              unpaidStudents={unpaidStudents}
              tomorrowExams={tomorrowExams}
              overrides={weekOverrides}
            />
          </div>

          <div className="mt-6">
            <TodayLessonsCard
              students={students}
              writtenStudentIds={todayWrittenIds}
              overrides={weekOverrides}
            />
          </div>

          <div className="mt-6">
            <WeekScheduleCard students={students} />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <RecentLessonsCard lessons={recentLessons} />
            </div>
            <div className="flex flex-col gap-6 lg:col-span-2">
              <UpcomingMakeupCard makeups={makeups} students={students} onAdded={load} />
              <UpcomingExamsCard exams={exams} />
              <QuickActionsCard />
            </div>
          </div>
        </>
      )}

      <MonthlyReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        teacherName={profile?.name ?? '선생님'}
        students={students}
      />
    </div>
  );
}